// My account's company sections (27-admin.md, "Account recovery", "Sign-in"; 21-user-settings.md,
// "Use desyr mail in other apps"; 23-mail.md, "Connect your mail" as v1's dialog). Each shows only
// where the company has what it needs; without the mail token on this device, one "Connect" card.
import type { MatrixClient } from 'matrix-js-sdk';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, CopyButton } from '../blocks/Button';
import { Confirm, Dialog, DialogButtons, FormDialog } from '../blocks/Dialog';
import { Field, PasswordField } from '../blocks/Field';
import { toast } from '../blocks/Toast';
import { LoadState, useLoad } from '../blocks/Wait';
import { Said, describe } from '../core/errors';
import { formatDate } from '../core/format';
import { jmap } from '../core/jmap';
import { connectMail, keep, mailAccess } from '../core/mail';
import { PRODUCT } from '../core/product';
import { clock } from '../core/settings';
import { fits, newKey, otpAddress } from '../core/totp';
import { t } from '../i18n';
import { admin, companyOf } from '../session/admin';
import { Card, Section } from '../settings/frame';
import { settingsIcons as icons } from '../settings/icons';
import { adminWords } from './words';
import { Codes } from './codes';

const hasToken = (client: MatrixClient) => !!mailAccess(client.getUserId()!);

/** "Your company account needs your password again on this device." with Connect. */
export function ConnectCard({ client, onConnected }: { client: MatrixClient; onConnected: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Card icon={icons.mail} title={t('mail.needsPassword')} control={<Button small onClick={() => setOpen(true)}>{t('mail.connect')}</Button>} />
      {open && <ConnectMail client={client} onClose={() => setOpen(false)} onConnected={() => (setOpen(false), onConnected())} />}
    </>
  );
}

/** "Connect your mail" (23, v1 dialog): the password, then the code with two-step sign-in. */
export function ConnectMail({ client, onClose, onConnected }: { client: MatrixClient; onClose: () => void; onConnected: () => void }) {
  const entry = companyOf(client)!;
  const email = mailAccess(client.getUserId()!)?.email ?? lastAddress(client);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState<string>();
  const connect = async () => {
    if (!password) throw new Said(t('mail.enterPassword'));
    if (code !== undefined && !/^\d{6}$/.test(code.replace(/\s/g, ''))) throw new Said(t('mail.enterCode'));
    const access = await connectMail(entry.password!, entry.mail!, email, password, code?.replace(/\s/g, '')).catch(() => {
      throw new Said(t('mail.unreachable'));
    });
    if (access === 'code') {
      if (code !== undefined) throw new Said(t('mail.wrongCode'));
      setCode('');
      throw new Said('');
    }
    if (!access) throw new Said(t(code !== undefined ? 'mail.wrongCode' : 'mail.wrongPassword'));
    keep(client.getUserId()!, access);
    onConnected();
  };
  return (
    <FormDialog title={t('mail.connectTitle')} action={t('mail.connect')} busyText={t('mail.connecting')} onClose={onClose} onSubmit={connect}>
      <p className="dialog-text">{t('mail.connectLine', { address: email })}</p>
      {code === undefined ? (
        <PasswordField autoFocus autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      ) : (
        <Field label={t('mail.code')} autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
      )}
    </FormDialog>
  );
}

/** The work address: the mail access's, else the chat ID written as an address. */
const lastAddress = (client: MatrixClient) => {
  const me = client.getUserId()!;
  return mailAccess(me)?.email ?? `${me.slice(1).split(':')[0]}@${me.split(':')[1]}`;
};

/** Account recovery: the private address and the codes left. */
export function RecoverySection({ client }: { client: MatrixClient }) {
  const entry = companyOf(client);
  const [connected, setConnected] = useState(hasToken(client));
  const [attempt, setAttempt] = useState(0);
  const [status, retry] = useLoad(() => (connected ? admin<{ email: string | null; codesLeft: number }>(client, '/me/recovery') : Promise.resolve(undefined)), [connected, attempt]);
  const [dialog, setDialog] = useState<'address' | 'codes'>();
  const [codes, setCodes] = useState<string[]>();
  if (!entry?.admin || !entry.mail) return null;
  if (!connected) return <Section title={t('recovery.title')}><ConnectCard client={client} onConnected={() => setConnected(true)} /></Section>;
  if (status.state !== 'done' || !status.value) return status.state === 'failed' ? null : <LoadState status={status} retry={retry} compact />;
  const { email, codesLeft } = status.value;
  return (
    <Section title={t('recovery.title')}>
      <div data-desyr-recovery="" className="settings-cards">
        <Card
          icon={icons.mail}
          title={t('recovery.address')}
          text={email ? t('recovery.addressLine', { address: email }) : t('recovery.noAddress')}
          control={<Button small onClick={() => setDialog('address')}>{t(email ? 'recovery.change' : 'recovery.add')}</Button>}
        />
        <Card
          icon={icons.shield}
          title={t('recovery.codes')}
          text={<span className={codesLeft <= 2 ? 'critical-text' : undefined}>{codesLeft ? t('recovery.codesLeft', { n: codesLeft }) : t('recovery.noCodes')}</span>}
          control={<Button small onClick={() => setDialog('codes')}>{t('recovery.newCodes')}</Button>}
        />
      </div>
      {dialog === 'address' && <AddressDialog client={client} current={email ?? ''} onClose={() => setDialog(undefined)} onDone={() => (setDialog(undefined), setAttempt((a) => a + 1))} />}
      {dialog === 'codes' && (
        <PasswordDialog
          title={t('recovery.newCodesTitle')}
          line={t('recovery.newCodesLine')}
          action={t('recovery.makeCodes')}
          busy={t('recovery.making')}
          onClose={() => setDialog(undefined)}
          send={async (password) => {
            const answer = await admin<{ codes: string[] }>(client, '/me/recovery/codes', { method: 'POST', body: { password } });
            setDialog(undefined);
            setCodes(answer.codes);
            setAttempt((a) => a + 1);
          }}
        />
      )}
      {codes && (
        <Dialog title={t('recovery.yourCodes')} required onClose={() => setCodes(undefined)}>
          <p className="dialog-text">{t('recovery.yourCodesLine')}</p>
          <Codes codes={codes} address={lastAddress(client)} />
          <DialogButtons>
            <Button variant="text" onClick={() => setCodes(undefined)}>
              {t('recovery.done')}
            </Button>
          </DialogButtons>
        </Dialog>
      )}
    </Section>
  );
}

/** A form dialog that needs the person's password (and maybe more). */
function PasswordDialog({ title, line, action, busy, children, onClose, send }: { title: string; line: string; action: string; busy: string; children?: ReactNode; onClose: () => void; send: (password: string) => Promise<unknown> }) {
  const [password, setPassword] = useState('');
  return (
    <FormDialog
      title={title}
      action={action}
      busyText={busy}
      onClose={onClose}
      onSubmit={async () => {
        if (!password) throw new Said(t('recovery.enterPassword'));
        await send(password).catch((e) => {
          throw new Said(adminWords(e, 'save'));
        });
      }}
    >
      <p className="dialog-text">{line}</p>
      {children}
      <Field label={t('recovery.yourPassword')} type="password" autoComplete="current-password" autoFocus={!children} value={password} onChange={(e) => setPassword(e.target.value)} />
    </FormDialog>
  );
}

function AddressDialog({ client, current, onClose, onDone }: { client: MatrixClient; current: string; onClose: () => void; onDone: () => void }) {
  const [address, setAddress] = useState(current);
  return (
    <PasswordDialog
      title={t('recovery.address')}
      line={t('recovery.addressDialogLine')}
      action={t('box.save')}
      busy={t('recovery.saving')}
      onClose={onClose}
      send={(password) => admin(client, '/me/recovery', { method: 'POST', body: { password, email: address.trim() } }).then(onDone)}
    >
      <Field label={t('recovery.address')} type="email" autoFocus value={address} onChange={(e) => setAddress(e.target.value)} />
    </PasswordDialog>
  );
}

const PASSWORD_GET = 'x:AccountPassword/get';
/** Mail in other apps words its password line by two-step sign-in's state. */
const TWO_STEP_CHANGED = 'desyr:two-step';
/** On when the one-time-code setting holds something (an empty one means off). */
const twoStepOn = (r: { list: { otpAuth?: unknown }[] }) => Object.keys((r.list?.[0]?.otpAuth as object | null) ?? {}).length > 0;
const PASSWORD_SET = 'x:AccountPassword/set';

/** Sign-in: two-step sign-in, read from and switched at the mail server. */
export function TwoStepSection({ client }: { client: MatrixClient }) {
  const me = client.getUserId()!;
  const entry = companyOf(client);
  const [connected, setConnected] = useState(hasToken(client));
  const [attempt, setAttempt] = useState(0);
  const [status] = useLoad(
    () => (connected ? jmap<{ list: { otpAuth?: unknown }[] }>(me, PASSWORD_GET, { ids: ['singleton'] }).then((r) => twoStepOn(r)) : Promise.resolve(undefined)),
    [connected, attempt],
  );
  const [dialog, setDialog] = useState<'on' | 'off'>();
  if (!entry?.mail) return null;
  if (!connected) return <Section title={t('twostep.section')}><ConnectCard client={client} onConnected={() => setConnected(true)} /></Section>;
  if (status.state !== 'done' || status.value === undefined) return null;
  const on = status.value;
  const changed = (now: boolean) => (setDialog(undefined), setAttempt((a) => a + 1), dispatchEvent(new Event(TWO_STEP_CHANGED)), toast(t(now ? 'twostep.onToast' : 'twostep.offToast')));
  const set = async (password: string, otpAuth: Record<string, unknown>) => {
    const answer = await jmap<{ notUpdated?: Record<string, unknown> }>(me, PASSWORD_SET, { update: { singleton: { currentSecret: password, otpAuth } } });
    if (answer.notUpdated?.singleton) throw Object.assign(new Error('refused'), { code: 'forbidden' });
  };
  return (
    <Section title={t('twostep.section')}>
      <div data-desyr-two-step={on ? 'on' : 'off'} className="settings-cards">
        <Card
          icon={icons.shield}
          title={t('twostep.title')}
          text={t(on ? 'twostep.onLine' : 'twostep.offLine')}
          control={<Button small onClick={() => setDialog(on ? 'off' : 'on')}>{t(on ? 'twostep.turnOff' : 'twostep.turnOn')}</Button>}
        />
      </div>
      {dialog === 'on' && <TurnOn email={lastAddress(client)} onClose={() => setDialog(undefined)} set={set} onDone={() => changed(true)} />}
      {dialog === 'off' && <TurnOff onClose={() => setDialog(undefined)} set={set} onDone={() => changed(false)} />}
    </Section>
  );
}

const wrongPassword = (e: unknown) => {
  throw new Said((e as { code?: string }).code === 'forbidden' ? t('twostep.notYourPassword') : describe(e, 'save').text);
};

function TurnOn({ email, onClose, set, onDone }: { email: string; onClose: () => void; set: (password: string, otp: Record<string, unknown>) => Promise<void>; onDone: () => void }) {
  const [key] = useState(newKey);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const address = otpAddress(key, PRODUCT, email);
  return (
    <FormDialog
      title={t('twostep.title')}
      action={t('twostep.turnOn')}
      busyText={t('twostep.turningOn')}
      onClose={onClose}
      onSubmit={async () => {
        // The code is checked here first, so a mistyped key can't lock anyone out.
        if (!(await fits(key, code))) throw new Said(t('twostep.codeDoesntFit'));
        if (!password) throw new Said(t('twostep.typePassword'));
        await set(password, { otpUrl: address }).catch(wrongPassword);
        onDone();
      }}
    >
      <p className="dialog-text">{t('twostep.onDialogLine')}</p>
      <div className="two-step-key">
        <code data-desyr-two-step-key="">{key.match(/.{1,4}/g)!.join(' ')}</code>
        <CopyButton small text={key} label={t('twostep.copyKey')} />
      </div>
      <a href={address}>{t('twostep.openApp')}</a>
      <Field label={t('twostep.code')} autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
      <Field label={t('recovery.yourPassword')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      <p className="settings-note">{t('twostep.lostPhone')}</p>
    </FormDialog>
  );
}

function TurnOff({ onClose, set, onDone }: { onClose: () => void; set: (password: string, otp: Record<string, unknown>) => Promise<void>; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  return (
    <FormDialog
      title={t('twostep.offTitle')}
      action={t('twostep.turnOff')}
      busyText={t('twostep.turningOff')}
      onClose={onClose}
      onSubmit={async () => {
        if (!password) throw new Said(t('twostep.typePassword'));
        await set(password, { otpUrl: null, otpCode: code.replace(/\s/g, '') }).catch((e) => {
          throw new Said((e as { code?: string }).code === 'forbidden' ? t('twostep.wrongPasswordOrCode') : describe(e, 'save').text);
        });
        onDone();
      }}
    >
      <p className="dialog-text">{t('twostep.offLine2')}</p>
      <Field label={t('recovery.yourPassword')} type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
      <Field label={t('twostep.code')} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
    </FormDialog>
  );
}

interface AppPassword {
  id: string;
  description: string;
  createdAt?: string;
  expiresAt?: string | null;
}

/** "Use desyr mail in other apps" (21, v1): the settings to copy, and the app passwords. */
export function MailAppsSection({ client }: { client: MatrixClient }) {
  const me = client.getUserId()!;
  const entry = companyOf(client);
  const host = entry?.mailHost;
  const [connected, setConnected] = useState(hasToken(client));
  const [attempt, setAttempt] = useState(0);
  const [list, retry] = useLoad(
    () =>
      connected
        ? jmap<{ list: AppPassword[] }>(me, 'x:AppPassword/get', { ids: null }).then((r) => [...(r.list ?? [])].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))))
        : Promise.resolve([]),
    [connected, attempt],
    'account',
  );
  const [twoStep, setTwoStep] = useState<boolean>();
  useEffect(() => {
    if (connected) void jmap<{ list: { otpAuth?: unknown }[] }>(me, PASSWORD_GET, { ids: ['singleton'] }).then((r) => setTwoStep(twoStepOn(r)), () => {});
  }, [connected, me, attempt]);
  useEffect(() => {
    const again = () => setAttempt((a) => a + 1);
    addEventListener(TWO_STEP_CHANGED, again);
    return () => removeEventListener(TWO_STEP_CHANGED, again);
  }, []);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<AppPassword>();
  if (!entry?.mail || !host) return null;
  const email = lastAddress(client);
  const rows: [string, string, string?][] = [
    [t('apps.server'), host],
    [t('apps.signInName'), email],
    [t('apps.caldav'), host],
    [t('apps.carddav'), host, t('apps.findThemselves')],
  ];
  const date = (iso?: string | null) => (iso ? formatDate(new Date(iso), clock().dateFormat) : '');
  return (
    <Section title={t('apps.title')}>
      <div data-desyr-mail-apps="" className="settings-cards">
        <Card icon={icons.mail} title={t('apps.title')} text={t('apps.line')}>
          <dl className="mail-apps">
            {rows.map(([label, value, note]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>
                  <code>{value}</code>
                  <CopyButton small text={value} name={t('apps.copy', { label })} />
                  {note && <small>{note}</small>}
                </dd>
              </div>
            ))}
          </dl>
          <p className="settings-note">{twoStep ? t('apps.twoStepOn') : t('apps.usePassword')}</p>
        </Card>
        {!connected ? (
          <ConnectCard client={client} onConnected={() => setConnected(true)} />
        ) : (
          <Card title={t('apps.passwords')} control={<Button small onClick={() => setCreating(true)}>{t('apps.create')}</Button>}>
            {list.state !== 'done' ? (
              <LoadState status={list} retry={retry} compact />
            ) : !list.value.length ? (
              <p className="settings-note">{t('apps.none')}</p>
            ) : (
              list.value.map((p) => (
                <div key={p.id} className="app-password" data-desyr-app-password={p.id}>
                  <span>
                    <strong>{p.description}</strong>
                    <small>
                      {t('apps.created', { date: date(p.createdAt) })}
                      {p.expiresAt && ` · ${t('apps.expires', { date: date(p.expiresAt) })}`}
                    </small>
                  </span>
                  <Button small variant="critical-text" aria-label={t('apps.revokeNamed', { name: p.description })} onClick={() => setRevoking(p)}>
                    {t('apps.revoke')}
                  </Button>
                </div>
              ))
            )}
          </Card>
        )}
      </div>
      {creating && <CreateAppPassword me={me} onClose={() => (setCreating(false), setAttempt((a) => a + 1))} />}
      {revoking && (
        <Confirm
          title={t('apps.revokeTitle', { name: revoking.description })}
          text={t('apps.revokeLine')}
          action={t('apps.revoke')}
          busyText={t('apps.revoking')}
          destructive
          context="save"
          onConfirm={async () => {
            await jmap(me, 'x:AppPassword/set', { destroy: [revoking.id] });
            toast(t('apps.revoked'));
            setAttempt((a) => a + 1);
          }}
          onClose={() => setRevoking(undefined)}
        />
      )}
    </Section>
  );
}

function CreateAppPassword({ me, onClose }: { me: string; onClose: () => void }) {
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<string>();
  const shown = useRef(false);
  if (secret)
    return (
      <Dialog title={t('apps.create')} required onClose={onClose}>
        <p className="dialog-text">{t('apps.shownOnce')}</p>
        <div className="two-step-key">
          <code aria-label={t('apps.password')}>{secret.match(/.{1,4}/g)!.join(' ')}</code>
          <CopyButton small text={secret} />
        </div>
        <DialogButtons>
          <Button variant="text" onClick={() => (setSecret(undefined), onClose())}>
            {t('recovery.done')}
          </Button>
        </DialogButtons>
      </Dialog>
    );
  return (
    <FormDialog
      title={t('apps.create')}
      action={t('apps.createButton')}
      busyText={t('apps.creating')}
      onClose={() => !shown.current && onClose()}
      onSubmit={async () => {
        if (!name.trim()) throw new Said(t('apps.giveName'));
        const answer = await jmap<{ created?: Record<string, { id: string; secret?: string }> }>(me, 'x:AppPassword/set', { create: { new: { description: name.trim() } } });
        const made = answer.created?.new;
        if (!made?.secret) throw new Error('not created');
        // The password is shown once, in place of the form; only Done closes it.
        shown.current = true;
        setSecret(made.secret);
      }}
    >
      <p className="dialog-text">{t('apps.createLine')}</p>
      <Field label={t('apps.name')} autoFocus placeholder={t('apps.namePlaceholder')} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
    </FormDialog>
  );
}
